import { Link } from 'react-router-dom';
import { ArrowLeft, Briefcase, MapPin, Clock, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_labresult-hub-1/artifacts/td8pjg41_FACTUAL%20LOGO.png";

const CareersPage = () => {
  const openPositions = [
    {
      title: 'Medical Laboratory Scientist',
      department: 'Laboratory',
      type: 'Full-time',
      location: 'Mowe, Ogun State',
      description: 'We are looking for a certified Medical Laboratory Scientist to join our team. Must have AIMLS/AMLSN certification.',
    },
    {
      title: 'Phlebotomist',
      department: 'Sample Collection',
      type: 'Full-time',
      location: 'Mowe, Ogun State',
      description: 'Seeking experienced phlebotomists for in-house and home sample collection services.',
    },
    {
      title: 'Front Desk Officer',
      department: 'Administration',
      type: 'Full-time',
      location: 'Mowe, Ogun State',
      description: 'Customer-focused individual to manage patient reception, appointments, and inquiries.',
    },
    {
      title: 'Laboratory Technician',
      department: 'Laboratory',
      type: 'Full-time',
      location: 'Mowe, Ogun State',
      description: 'Support laboratory operations with sample processing and equipment maintenance.',
    }
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-slate-900">
      {/* Header */}
      <header className="bg-slate-900 text-white py-4">
        <div className="container mx-auto px-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Logo" className="w-10 h-10 rounded-full" />
            <span className="font-bold text-lg">Factual Medical Diagnostic Centre</span>
          </Link>
          <Link to="/">
            <Button variant="ghost" className="text-white hover:text-sky-300">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-gradient-to-br from-emerald-600 to-emerald-800 text-white py-20">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Join Our Team</h1>
          <p className="text-xl text-emerald-100 max-w-2xl mx-auto">
            Build your career with Nigeria's trusted medical diagnostic centre
          </p>
        </div>
      </section>

      {/* Why Join Us */}
      <section className="py-16 bg-slate-50 dark:bg-slate-800">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-slate-900 dark:text-white text-center mb-12">Why Work With Us</h2>
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {[
              { title: 'Professional Growth', desc: 'Continuous learning opportunities and career advancement programs.' },
              { title: 'Competitive Benefits', desc: 'Attractive salary packages with health insurance and allowances.' },
              { title: 'Modern Environment', desc: 'Work with state-of-the-art equipment in a supportive atmosphere.' }
            ].map((item, i) => (
              <div key={i} className="bg-white dark:bg-slate-900 p-6 rounded-xl shadow-sm text-center">
                <h3 className="font-bold text-slate-900 dark:text-white mb-2">{item.title}</h3>
                <p className="text-slate-600 dark:text-slate-400 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Open Positions */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-8">Open Positions</h2>
          <div className="space-y-4">
            {openPositions.map((job, i) => (
              <div key={i} className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-6 hover:shadow-md transition-shadow">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div>
                    <h3 className="text-xl font-bold text-slate-900 dark:text-white">{job.title}</h3>
                    <div className="flex flex-wrap gap-2 mt-2">
                      <Badge variant="outline" className="text-sky-600 border-sky-200">
                        <Briefcase className="w-3 h-3 mr-1" />
                        {job.department}
                      </Badge>
                      <Badge variant="outline" className="text-emerald-600 border-emerald-200">
                        <Clock className="w-3 h-3 mr-1" />
                        {job.type}
                      </Badge>
                      <Badge variant="outline" className="text-amber-600 border-amber-200">
                        <MapPin className="w-3 h-3 mr-1" />
                        {job.location}
                      </Badge>
                    </div>
                    <p className="text-slate-600 dark:text-slate-400 mt-3">{job.description}</p>
                  </div>
                  <Button className="bg-sky-600 hover:bg-sky-700 flex-shrink-0">
                    Apply Now
                    <Send className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            ))}
          </div>

          {/* Application Info */}
          <div className="mt-12 bg-sky-50 dark:bg-sky-900/30 p-6 rounded-lg">
            <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-4">How to Apply</h3>
            <p className="text-slate-600 dark:text-slate-400 mb-4">
              Send your CV and cover letter to our HR department:
            </p>
            <p className="text-sky-600 dark:text-sky-400 font-semibold">FACTUALMEDICALDIAGNOSTICCENTRE@GMAIL.COM</p>
            <p className="text-slate-500 dark:text-slate-500 mt-2 text-sm">
              Please include the position title in your email subject line.
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-8">
        <div className="container mx-auto px-4 text-center">
          <p className="text-slate-400">&copy; {new Date().getFullYear()} Factual Medical Diagnostic Centre. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default CareersPage;
